import { readonly, ref } from 'vue';
import { profileService } from '../services/profileService.js';
import { usersService } from '../services/usersService.js';
import { clearJournalResponses } from '../utils/journalResponseCache.js';
import { clearStatisticsViews, statisticsViewerKey } from '../utils/statisticsViewCache.js';
import { clearHomeSnapshots } from '../utils/homeSnapshotCache.js';
import { clearAvatarSnapshots, readAvatarSnapshot, writeAvatarSnapshot } from '../utils/avatarSnapshotCache.js';
import { clearBotPreviews } from '../utils/botPreviewCache.js';

const USER_CONTEXT_STORAGE_KEY = 'corecry:user-context';
const USER_AVATAR_STORAGE_KEY = 'corecry:user-avatar';

const userContext = ref(null);
const userAvatar = ref('');
let inFlight = null;
let contextRevision = 0;
let avatarInFlight = null;
let avatarRevision = 0;
export const getUserContextRevision = () => contextRevision;

const getSessionStorage = () => {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const readCachedValue = (key) => {
    const storage = getSessionStorage();
    if (!storage) return null;
    try {
        return JSON.parse(storage.getItem(key));
    } catch {
        return null;
    }
};

const writeCachedValue = (key, value) => {
    const storage = getSessionStorage();
    if (!storage) return;
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch {
        // Session storage is an optional rendering cache.
    }
};

const cacheableUserContext = (data) => ({
    user_id: data.user_id || '',
    username: data.username || '',
    registered_at: data.registered_at || '',
    is_owner: Boolean(data.is_owner),
    switched_by_admin: Boolean(data.switched_by_admin)
});

const cachedContext = readCachedValue(USER_CONTEXT_STORAGE_KEY);
if (cachedContext?.user_id) {
    userContext.value = cacheableUserContext(cachedContext);
}
let cachedAvatar = readCachedValue(USER_AVATAR_STORAGE_KEY);

export const buildUserContextKey = (user) => {
    if (!user?.user_id) return null;
    const impersonation = user.switched_by_admin ? 'impersonated' : 'direct';
    return `${String(user.user_id)}:${impersonation}`;
};

export const loadUserContext = async ({ force = false } = {}) => {
    if (!force && userContext.value) return userContext.value;
    if (inFlight) return inFlight;

    const revision = contextRevision;
    const request = usersService.info({ credentials: 'include' })
        .then((data) => {
            if (revision !== contextRevision) throw new DOMException('User context changed', 'AbortError');
            if (buildUserContextKey(userContext.value) !== buildUserContextKey(data)) {
                clearJournalResponses(buildUserContextKey(userContext.value));
                if (userContext.value) { clearHomeSnapshots(); clearAvatarSnapshots(); clearBotPreviews(); }
                avatarRevision++;
                userAvatar.value = '';
                writeCachedValue(USER_AVATAR_STORAGE_KEY, '');
            }
            if (statisticsViewerKey(userContext.value) !== statisticsViewerKey(data)) clearStatisticsViews();
            userContext.value = cacheableUserContext(data);
            if (!userAvatar.value) userAvatar.value = readAvatarSnapshot(buildUserContextKey(data)) ||
                (buildUserContextKey(cachedContext) === buildUserContextKey(data) && typeof cachedAvatar === 'string' ? cachedAvatar : '');
            if (userAvatar.value && globalThis.performance?.mark) performance.mark('corecry-avatar-cache-restored');
            cachedAvatar = null;
            writeCachedValue(USER_CONTEXT_STORAGE_KEY, userContext.value);
            return data;
        })
        .finally(() => {
            if (inFlight === request) inFlight = null;
        });
    inFlight = request;
    return inFlight;
};

export const setUserAvatar = (avatar) => {
    userAvatar.value = avatar || '';
    writeCachedValue(USER_AVATAR_STORAGE_KEY, userAvatar.value);
    const revision = ++avatarRevision;
    const identity = buildUserContextKey(userContext.value);
    void writeAvatarSnapshot(identity, userAvatar.value, () => revision === avatarRevision && identity === buildUserContextKey(userContext.value));
};

export const loadUserAvatar = async ({ force = false } = {}) => {
    if (!force && typeof userAvatar.value === 'string' && userAvatar.value) {
        return userAvatar.value;
    }
    if (avatarInFlight) return avatarInFlight;

    const revision = contextRevision;
    const identity = buildUserContextKey(userContext.value);
    const request = profileService.avatar({ credentials: 'include' })
        .then((data) => {
            if (revision !== contextRevision || identity !== buildUserContextKey(userContext.value)) throw new DOMException('User context changed', 'AbortError');
            setUserAvatar(data.avatar || '');
            if (globalThis.performance?.mark) performance.mark('corecry-avatar-response-ready');
            return userAvatar.value;
        })
        .finally(() => {
            if (avatarInFlight === request) avatarInFlight = null;
        });

    avatarInFlight = request;
    return request;
};

export const clearUserContext = () => {
    contextRevision++;
    avatarRevision++;
    cachedAvatar = null;
    clearJournalResponses();
    clearStatisticsViews();
    clearHomeSnapshots();
    clearAvatarSnapshots();
    clearBotPreviews();
    userContext.value = null;
    userAvatar.value = '';
    inFlight = null;
    avatarInFlight = null;
    const storage = getSessionStorage();
    if (storage) {
        storage.removeItem(USER_CONTEXT_STORAGE_KEY);
        storage.removeItem(USER_AVATAR_STORAGE_KEY);
    }
};

export function useUserContext() {
    return {
        userContext: readonly(userContext),
        userAvatar: readonly(userAvatar),
        loadUserContext,
        loadUserAvatar,
        setUserAvatar,
        clearUserContext,
        buildUserContextKey
    };
}
